import type {
    CreateExtArgs,
    CreateExtensionArgument,
    Extension,
    ExtensionAPI,
    ResourceLoaderArgs,
    ResourceLoaderDefinition
} from '@wix/document-manager-core'
import _ from 'lodash'
import type {RMApi} from './rendererModel'
import type {Reference, SchemaCollection} from './csave/serverProtocolTypes'
import type {ClientSpecMap, EditorClientSpecMapEntry} from '@wix/document-services-types'
import {RESOURCE_TYPES} from '../constants/constants'
import {getOverrideId} from '../utils/resourceLoaderUtils'
import {registerSchemaCollection} from '../utils/schemaUtils'
import type {ManifestUpdateMessage} from './distributor/messages'
import {ReportableError} from '@wix/document-manager-utils'

const INTERACTIONS = {
    LOAD_SCHEMAS: 'load-schemas',
    REGISTER_SCHEMAS: 'register-schemas',
    BATCH_REGISTER_IN_DM: 'batch-register-schemas-dm',
    UPDATE_SCHEMA_REFS_TO_VIEWER: 'update-schema-refs-to-viewer',
    SCHEMA_RESOURCES_LOADING_FAILED: 'schema-resources-loading-failed'
}
export const MANIFEST_UPDATE_MESSAGE: ManifestUpdateMessage['messageType'] = 'MANIFEST_UPDATE'

export interface SchemaResourceLoaderExtensionAPI extends ExtensionAPI {
    schemaResourceLoader: {
        sendStoredReferencesToViewer(): void
        registerSchemaCollections(schemaCollections: SchemaCollection[]): void
    }
}

export interface SchemaCollectionServer {
    getSchemaCollectionsByAppIds(appDefinitionIds: string[], overrideIds?: string[]): Promise<SchemaCollection[]>
}

type TypeToReferencesMap = Record<string, Reference[]>
type NamespaceToReferencesMap = Record<string, TypeToReferencesMap>
export type SchemaCollectionReferencesMap = Record<string, NamespaceToReferencesMap>

const EVENTS = {
    SCHEMA_RESOURCE_LOADER: {
        DYNAMIC_REFERENCES_UPDATE_EVENT: 'DYNAMIC_REFERENCES_UPDATE_EVENT'
    }
}

const mergeReferencesIntoMap = (
    referencesMap: SchemaCollectionReferencesMap,
    appId: string,
    references: NamespaceToReferencesMap | undefined
) => {
    if (!references) return

    if (!referencesMap[appId]) {
        referencesMap[appId] = {}
    }

    for (const [namespace, typeToReferencesMap] of Object.entries(references)) {
        if (!_.isEmpty(typeToReferencesMap)) {
            if (!referencesMap[appId][namespace]) {
                referencesMap[appId][namespace] = {}
            }
            Object.assign(referencesMap[appId][namespace], typeToReferencesMap)
        }
    }
}

export const createExtension = ({
    environmentContext,
    logger,
    dsConfig,
    experimentInstance
}: CreateExtensionArgument): Extension => {
    let storedReferences: SchemaCollectionReferencesMap = {}
    let viewerIsInitialized = false

    const createResourceLoader = (args: ResourceLoaderArgs): ResourceLoaderDefinition[] => {
        const {dal, pointers, extensionAPI} = args
        const {schemaResourceLoader} = extensionAPI as SchemaResourceLoaderExtensionAPI
        const {rendererModel} = args.extensionAPI as RMApi

        const load = async (appDefIds: string[]) => {
            if (!appDefIds.length) {
                return
            }

            const overrideIds: string[] = []
            for (const appDefId of appDefIds) {
                const overrideId = getOverrideId(dal, pointers, appDefId)
                if (overrideId) overrideIds.push(overrideId)
            }

            try {
                logger.interactionStarted(INTERACTIONS.LOAD_SCHEMAS)

                const allSchemaCollections: SchemaCollection[] =
                    await environmentContext.serverFacade.getSchemaCollectionsByAppIds(
                        appDefIds,
                        overrideIds.length > 0 ? overrideIds : undefined
                    )
                logger.interactionStarted(INTERACTIONS.REGISTER_SCHEMAS)
                schemaResourceLoader.registerSchemaCollections(allSchemaCollections)

                logger.interactionEnded(INTERACTIONS.REGISTER_SCHEMAS)
                logger.interactionEnded(INTERACTIONS.LOAD_SCHEMAS)
            } catch (error) {
                logger.captureError(
                    new ReportableError({
                        errorType: INTERACTIONS.SCHEMA_RESOURCES_LOADING_FAILED,
                        message: `Resources loading failed for appIds: ${appDefIds}`,
                        tags: {
                            appDefIds
                        },
                        extras: {
                            originalError: error
                        }
                    })
                )
                throw error
            }
        }

        const getAvailableResources = () => {
            const clientSpecMap = rendererModel.getClientSpecMap() as ClientSpecMap
            if (!clientSpecMap) {
                return []
            }

            const clientSpecMapAppIds = _.uniq(
                Object.values(clientSpecMap)
                    .map(appSpec => (appSpec as EditorClientSpecMapEntry).appDefinitionId)
                    .filter(Boolean)
            )

            return _.uniq(clientSpecMapAppIds)
        }

        const getEditorAndHybridResources = () => {
            const clientSpecMap = rendererModel.getClientSpecMap() as ClientSpecMap

            if (!clientSpecMap) {
                return []
            }

            const clientSpecMapAppIds = _.uniq(
                Object.values(clientSpecMap)
                    .filter(appSpec => {
                        const isEditorOrHybrid = ['Editor', 'Hybrid'].includes(
                            (appSpec as EditorClientSpecMapEntry).appType
                        )
                        const appDefId = (appSpec as EditorClientSpecMapEntry).appDefinitionId
                        const hasOverrideId = appDefId && !!getOverrideId(dal, pointers, appDefId)
                        return isEditorOrHybrid || hasOverrideId
                    })
                    .map(appSpec => (appSpec as EditorClientSpecMapEntry).appDefinitionId)
                    .filter(Boolean)
            )

            return _.uniq(clientSpecMapAppIds)
        }

        const loadAll = async () => {
            if (!experimentInstance.isOpen('dm_schemaResourceLoader') || !rendererModel.isBuilderComponentModel()) {
                return
            }
            await load(getEditorAndHybridResources())
        }

        const initAvailableResources = async () => {}

        return [
            {
                resourceType: RESOURCE_TYPES.SCHEMA,
                load,
                loadAll,
                getAvailableResources,
                initAvailableResources
            }
        ]
    }

    const createExtensionAPI = ({eventEmitter, coreConfig}: CreateExtArgs): SchemaResourceLoaderExtensionAPI => {
        return {
            schemaResourceLoader: {
                sendStoredReferencesToViewer: () => {
                    if (!_.isEmpty(storedReferences)) {
                        eventEmitter.emit(
                            EVENTS.SCHEMA_RESOURCE_LOADER.DYNAMIC_REFERENCES_UPDATE_EVENT,
                            storedReferences
                        )
                    }
                    viewerIsInitialized = true
                    storedReferences = {}
                },
                registerSchemaCollections: (schemaCollections: SchemaCollection[]) => {
                    const referencesMap: SchemaCollectionReferencesMap = {}
                    logger.interactionStarted(INTERACTIONS.BATCH_REGISTER_IN_DM)
                    coreConfig.schemaService.batchRegisterSchemas(() => {
                        for (const schemaCollection of schemaCollections) {
                            const {innerElements, appId} = schemaCollection
                            const topLevelComponentReferences = registerSchemaCollection(
                                dsConfig,
                                experimentInstance,
                                coreConfig.schemaService,
                                schemaCollection
                            )
                            mergeReferencesIntoMap(referencesMap, appId!, topLevelComponentReferences)
                            if (innerElements) {
                                for (const innerElement of innerElements) {
                                    const innerElementReferences = registerSchemaCollection(
                                        dsConfig,
                                        experimentInstance,
                                        coreConfig.schemaService,
                                        innerElement
                                    )
                                    mergeReferencesIntoMap(referencesMap, appId!, innerElementReferences)
                                }
                            }
                        }
                    })
                    logger.interactionEnded(INTERACTIONS.BATCH_REGISTER_IN_DM)

                    if (!_.isEmpty(referencesMap)) {
                        if (viewerIsInitialized) {
                            logger.interactionStarted(INTERACTIONS.UPDATE_SCHEMA_REFS_TO_VIEWER)
                            eventEmitter.emit(
                                EVENTS.SCHEMA_RESOURCE_LOADER.DYNAMIC_REFERENCES_UPDATE_EVENT,
                                referencesMap
                            )
                            logger.interactionEnded(INTERACTIONS.UPDATE_SCHEMA_REFS_TO_VIEWER)
                        } else if (coreConfig.withoutViewer) {
                            // no one needs references map
                        } else {
                            storedReferences = referencesMap
                        }
                    }
                }
            }
        }
    }

    return {
        name: 'schemaResourceLoader',
        dependencies: new Set(['schema', 'rendererModel', 'documentServicesModel']),
        createResourceLoader,
        createExtensionAPI,
        EVENTS
    }
}
